import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Briefcase, 
  Users,
  TrendingUp,
  DollarSign,
  CheckCircle2,
  Loader2,
  Rocket,
  Target,
  Award
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const universities = [
  "University of Virginia (UVA)",
  "George Mason University",
  "Virginia Tech",
  "Virginia Commonwealth University (VCU)",
  "University of Maryland (UMD)",
  "Georgetown University",
  "George Washington University (GWU)",
  "Johns Hopkins University",
  "Duke University",
  "North Carolina State University (NC State)",
  "Other"
];

const benefits = [
  {
    title: "Earn While You Learn",
    description: "Competitive pay with flexible hours that work around your class schedule.",
    icon: DollarSign
  },
  {
    title: "Real Experience",
    description: "Gain hands-on experience in operations, marketing, and entrepreneurship.",
    icon: TrendingUp
  },
  {
    title: "Build Your Network",
    description: "Connect with students across campuses and work with a growing startup.",
    icon: Users
  },
  {
    title: "Growth Opportunities",
    description: "Top performers can advance to leadership roles as we expand.",
    icon: Rocket
  }
];

const responsibilities = [
  "Promote Student Store It on your campus",
  "Help coordinate storage pickups and deliveries",
  "Support student outreach and marketing efforts",
  "Assist with campus-level operations",
  "Provide feedback to improve our service"
];

export const Careers = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    university: '',
    graduation_year: '',
    major: '',
    why_interested: '',
    experience: '',
    availability: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/applications`, formData);
      setSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center p-4" data-testid="careers-success">
        <motion.div 
          className="bg-white rounded-2xl p-8 md:p-12 text-center max-w-lg shadow-xl"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <h2 
            className="text-3xl font-bold text-navy mb-4"
            style={{ fontFamily: 'Chivo, sans-serif' }}
          >
            Application Received!
          </h2>
          <p className="text-gray-600 mb-6">
            Thank you for applying to join the Student Store It Campus Team. 
            We'll review your application and get back to you soon.
          </p>
          <Button 
            onClick={() => setSubmitted(false)}
            className="btn-primary"
            data-testid="apply-again-btn"
          >
            Submit Another Application
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="careers-page">
      {/* Hero Section - White */}
      <section className="bg-white section-padding border-b border-gray-100" data-testid="careers-hero">
        <div className="section-container">
          <motion.div 
            className="max-w-3xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="text-sm font-bold uppercase tracking-widest text-red-600 mb-4 block">
              Join Our Team
            </span>
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-black text-navy mb-6 leading-tight"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              Join the Student Store It Campus Team
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              We are building campus teams and looking for motivated students who want to gain 
              real-world experience in marketing, operations, and entrepreneurship while earning income.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-padding bg-gray-50" data-testid="benefits-section">
        <div className="section-container">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 
              className="text-3xl md:text-4xl font-bold text-navy mb-4"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              Why Join Us?
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <div className="w-16 h-16 mx-auto bg-navy/5 rounded-2xl flex items-center justify-center mb-4">
                  <benefit.icon className="w-8 h-8 text-navy" />
                </div>
                <h3 
                  className="text-lg font-bold text-navy mb-2"
                  style={{ fontFamily: 'Chivo, sans-serif' }}
                >
                  {benefit.title}
                </h3>
                <p className="text-gray-600 text-sm">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Role Description */}
      <section className="section-padding bg-white" data-testid="role-section">
        <div className="section-container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-sm font-bold uppercase tracking-widest text-red-accent mb-4 block">
                Campus Operations Role
              </span>
              <h2 
                className="text-3xl md:text-4xl font-bold text-navy mb-6"
                style={{ fontFamily: 'Chivo, sans-serif' }}
              >
                What You'll Do
              </h2>
              <p className="text-gray-600 text-lg mb-6 leading-relaxed">
                Campus representatives help grow the brand, support student outreach, and assist 
                with campus-level operations. It's a great opportunity to build skills while 
                helping fellow students.
              </p>
              
              <div className="space-y-4">
                {responsibilities.map((item, index) => (
                  <motion.div
                    key={index}
                    className="flex items-start gap-3"
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                  >
                    <Target className="w-5 h-5 text-red-accent flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img 
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=1000&auto=format&fit=crop"
                alt="Team collaboration"
                className="rounded-2xl shadow-xl w-full h-[400px] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-white rounded-xl p-4 shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-navy rounded-full flex items-center justify-center">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-navy">Great for Resume</p>
                    <p className="text-xs text-gray-500">Real startup experience</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="section-padding bg-gray-50" data-testid="application-form-section">
        <div className="section-container">
          <motion.div 
            className="max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="text-center mb-8">
              <Briefcase className="w-12 h-12 text-navy mx-auto mb-4" />
              <h2 
                className="text-3xl md:text-4xl font-bold text-navy mb-4"
                style={{ fontFamily: 'Chivo, sans-serif' }}
              >
                Apply Now
              </h2>
              <p className="text-gray-600">
                Fill out the form below to apply for the Campus Operations role.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6" data-testid="application-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First Name *</Label>
                  <Input
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                    placeholder="John"
                    data-testid="input-first-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Last Name *</Label>
                  <Input
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                    placeholder="Doe"
                    data-testid="input-last-name"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="john@university.edu"
                    data-testid="input-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="(555) 123-4567"
                    data-testid="input-phone"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="university">University *</Label>
                <Select 
                  value={formData.university} 
                  onValueChange={(value) => handleSelectChange('university', value)}
                >
                  <SelectTrigger data-testid="select-university">
                    <SelectValue placeholder="Select your university" />
                  </SelectTrigger>
                  <SelectContent>
                    {universities.map((uni) => (
                      <SelectItem key={uni} value={uni}>
                        {uni}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="graduation_year">Graduation Year *</Label>
                  <Select 
                    value={formData.graduation_year} 
                    onValueChange={(value) => handleSelectChange('graduation_year', value)}
                  >
                    <SelectTrigger data-testid="select-graduation-year">
                      <SelectValue placeholder="Select year" />
                    </SelectTrigger>
                    <SelectContent>
                      {['2025', '2026', '2027', '2028', '2029'].map((year) => (
                        <SelectItem key={year} value={year}>
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="major">Major *</Label>
                  <Input
                    id="major"
                    name="major"
                    value={formData.major}
                    onChange={handleChange}
                    required
                    placeholder="Business Administration"
                    data-testid="input-major"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="availability">Availability *</Label>
                <Select 
                  value={formData.availability} 
                  onValueChange={(value) => handleSelectChange('availability', value)}
                >
                  <SelectTrigger data-testid="select-availability">
                    <SelectValue placeholder="Select your availability" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full-time-summer">Full-time (Summer)</SelectItem>
                    <SelectItem value="part-time-summer">Part-time (Summer)</SelectItem>
                    <SelectItem value="year-round">Year-round availability</SelectItem>
                    <SelectItem value="flexible">Flexible schedule</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="why_interested">Why are you interested in this role? *</Label>
                <Textarea
                  id="why_interested"
                  name="why_interested"
                  value={formData.why_interested}
                  onChange={handleChange}
                  required
                  placeholder="Tell us why you want to join the Student Store It team..."
                  rows={4}
                  data-testid="textarea-why-interested"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="experience">Relevant Experience (Optional)</Label>
                <Textarea
                  id="experience"
                  name="experience"
                  value={formData.experience}
                  onChange={handleChange}
                  placeholder="Share any relevant experience in marketing, operations, or student organizations..."
                  rows={3}
                  data-testid="textarea-experience"
                />
              </div>

              <Button 
                type="submit" 
                className="btn-accent w-full py-4"
                disabled={isSubmitting}
                data-testid="submit-application-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Briefcase className="w-4 h-4 mr-2" />
                    Apply for Campus Operations Role
                  </>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Careers;
